import * as React from 'react';
import './fileField.less';
import { FileUploadType, TemporaryFile, FileAction } from './types';
import { CoreHelper } from 'ff-shared-components-core';
// [ComponentUsage]
// <FileField
// buttonText="Select"
// fileFieldLabel="Hello"
// maxSize={10000000}
// fileId={1}
// validExtensions={['application/pdf']}
// handleFile={this.handleFile}
// />

const VALID_EXTENSIONS = [
	'application/pdf',
	'image/jpeg',
	'image/png',
	'application/octet-stream',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
// const VALID_EXTENSIONS = ['application/pdf'];

export interface FileFieldProps {
	/** Name/Id of FileField */
	fileFieldId?: string;
	/** Label above the file field */
	fileFieldLabel?: string;
	/** Set the text of the green button */
	buttonText?: string;
	/** Set the text of the green button */
	buttonMultipleFileText?: string;
	/** Set the max size of file */
	filesMaxSize?: number;
	/** Number of files allowed */
	nrAllowedFiles: number;
	/** Set the validExtensions of files */
	validExtensions?: string[];
	/** Boolean for controlling if is required or not */
	isRequired?: boolean;
	/** Set files list */
	filesList?: FileUploadType[];
	/** Set the loading for example while uploading the file */
	handleFile?(files: TemporaryFile, action: FileAction): void;
	/** Boolean for controlling if exist or not an error */
	hasError?: boolean;
	/** Error message shown in case it exists any */
	errorMessage?: string;
	/** Show modal attachments number error */
	showAttachmentsNumberInfoModal?(text: string): void;
	/** Show modal file size error */
	showAttachmentSizeInfoModal?(text: string): void;
	/** Prop to control if is disabled */
	isDisabled?: boolean;
	/** Message if props disable is true */
	disableMessage?: string;
	/**sets a popup */
	tooltip?: () => React.ReactNode;
	/** Prop to control if link is downloadable or not */
	isDownloadable?: boolean;
	/**Function to download file */
	downloadFile?(id: string): void;
}

export interface FileFieldState {
	files: FileUploadType[];
	hasFiles: boolean;
	hasMultipleFiles: boolean;
}

export class FileField extends React.Component<FileFieldProps, FileFieldState> {
	constructor(props: FileFieldProps) {
		super(props);
		const { filesList } = this.props;

		let files: FileUploadType[] = [];
		let hasFiles = false;

		if (filesList) {
			files = filesList;
			hasFiles = files.length > 0 ? true : false;
		}

		this.state = { files, hasFiles, hasMultipleFiles: props.nrAllowedFiles > 1 };
	}

	render(): JSX.Element {
		const {
			fileFieldId,
			fileFieldLabel,
			buttonText,
			buttonMultipleFileText,
			validExtensions = VALID_EXTENSIONS,
			isRequired,
			hasError = false,
			errorMessage,
			nrAllowedFiles,
			isDisabled = false,
			disableMessage,
			tooltip,
			isDownloadable = false,
			downloadFile,
		} = this.props;
		const { files, hasMultipleFiles } = this.state;

		return (
			<div id={fileFieldId} className={'it-file-field'}>
				<div>
					<label
						style={{ paddingRight: '0.5rem' }}
						className={`fileFieldLabel  ${isRequired ? 'required' : ''}`}
					>
						{fileFieldLabel}
					</label>
					{tooltip && tooltip()}
				</div>

				<div className="ds-file-chooser multiline">
					<div className="inputBorder">
						{!isDisabled &&
							files &&
							files.map(f => (
								<span
									key={f.id}
									className={`${isDownloadable ? 'ds-text-link-appearance-2 ' : 'ds-text-link'}`}
									onClick={() => isDownloadable && downloadFile && downloadFile(f.id)}
								>
									{f.name}
									<i
										className="ic-reject-small-before"
										onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>): void => {
											e.stopPropagation();
											this.removeFile({ id: f.id, name: f.name });
										}}
									></i>
								</span>
							))}
						{isDisabled && <span className="ds-text-link">{disableMessage}</span>}
					</div>

					<label
						id={`'file-upload-label-'${fileFieldId}`}
						htmlFor={`'file-upload-'${fileFieldId}`}
						className={`${
							files.length === nrAllowedFiles || isDisabled ? 'file-btn disabled' : 'file-btn'
						}`}
					>
						{nrAllowedFiles > 1 && files.length > 0 && buttonMultipleFileText
							? buttonMultipleFileText
							: buttonText}
					</label>

					<input
						id={`'file-upload-'${fileFieldId}`}
						type="file"
						multiple={hasMultipleFiles}
						onChange={this.handleChange}
						style={{ display: 'none' }}
						disabled={files.length === nrAllowedFiles || isDisabled}
						accept={validExtensions.toString()}
					/>
				</div>
				<div className="input-error-text">
					<span>{`${hasError ? errorMessage : ''}`}</span>
				</div>
			</div>
		);
	}

	// eslint-disable-next-line sonarjs/cognitive-complexity
	private handleChange = (e: React.ChangeEvent<any>): void => {
		const {
			nrAllowedFiles = 1,
			filesMaxSize = 999999,
			validExtensions = VALID_EXTENSIONS,
			showAttachmentsNumberInfoModal,
			showAttachmentSizeInfoModal,
		} = this.props;
		const { files } = this.state;

		const savedFiles: TemporaryFile[] = [];
		const auxFiles: any = e.target.files;
		const length: number = auxFiles.length;
		let error = 0;
		let base64File: string;

		if (nrAllowedFiles === 1) {
			this.cleanFiles();
		}

		if ((nrAllowedFiles >= length + files.length && nrAllowedFiles > 1) || nrAllowedFiles === 1) {
			for (let i = 0; i < auxFiles.length; i++) {
				const file = auxFiles[i];
				error = 0;
				base64File = '';

				if (!file) {
					return;
				}

				if (validExtensions.indexOf(file.type) === -1) {
					error = 1;
				}

				if (nrAllowedFiles < auxFiles.length) {
					error = 1;
				}

				if (file.size > filesMaxSize) {
					error = 2;
					showAttachmentSizeInfoModal && showAttachmentSizeInfoModal('modals.messages.attachmentSizeWarning');
				}

				if (!error) {
					const maxWidth = 1500;
					const maxHeight = 1500;

					const reader = new FileReader();

					// eslint-disable-next-line no-loop-func
					reader.onload = (event): void => {
						const target: any = event.target;
						const dataUrl: string = target.result;

						if (
							file.type === 'application/pdf' ||
							file.type === 'application/octet-stream' ||
							file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
						) {
							// Since it contains the Data URI, we should remove the prefix and keep only Base64 string.
							base64File = dataUrl.replace(/^data:.+;base64,/, '');

							savedFiles.push({ id: CoreHelper.guid(), error, file, base64File });
						} else {
							const image = new Image();
							image.src = dataUrl;

							const canvas = document.createElement('canvas');

							image.onload = (): void => {
								let width = image.width;
								let height = image.height;

								if (width > height) {
									if (width > maxWidth) {
										height = Math.round((height * maxWidth) / width);
										width = maxWidth;
									}
								} else {
									if (height > maxHeight) {
										width = Math.round((width * maxHeight) / height);
										height = maxHeight;
									}
								}

								canvas.width = width;
								canvas.height = height;

								const ctx = canvas.getContext('2d');
								if (ctx == null) {
									return;
								}

								ctx.drawImage(image, 0, 0, width, height);

								const auxBase64File = canvas.toDataURL('image/jpeg', 0.7);
								base64File = auxBase64File.replace(/^data:.+;base64,/, '');

								savedFiles.push({ id: CoreHelper.guid(), error, file, base64File });

								if (savedFiles.length === length && savedFiles.length > 0) {
									this.saveFiles(savedFiles);
								}
							};
						}

						if (savedFiles.length === length && savedFiles.length > 0) {
							this.saveFiles(savedFiles);
						}
					};

					reader.readAsDataURL(file);
				} else {
					savedFiles.push({ id: CoreHelper.guid(), error: error, file: {}, base64File: '' });
				}
			}
		} else {
			showAttachmentsNumberInfoModal && showAttachmentsNumberInfoModal('modals.messages.attachmentsWarning');
		}

		e.target.value = '';
	};

	private saveFiles = (filesSave: TemporaryFile[]): void => {
		const { handleFile } = this.props;
		const { files } = this.state;

		const auxF = files;

		if (filesSave.length > 0) {
			filesSave.forEach(f => {
				auxF.push({ id: f.id, name: f.file.name, base64File: f.base64File });
				handleFile && handleFile(f, FileAction.Add);
			});

			this.setState({ files: auxF, hasFiles: true });
		} else {
			const auxHasFiles: boolean = auxF.length > 0;
			this.setState({ hasFiles: auxHasFiles });
		}
	};

	private removeFile = (fileRemove: FileUploadType): void => {
		const { handleFile } = this.props;
		const { files } = this.state;

		const auxF = files.filter(x => x.id !== fileRemove.id);

		const auxHasFiles: boolean = auxF.length > 0;

		this.setState({ files: auxF, hasFiles: auxHasFiles });

		handleFile &&
			handleFile(
				{ id: fileRemove.id, error: 0, file: { name: fileRemove.name }, base64File: '' },
				FileAction.Remove
			);
	};

	private cleanFiles = (): void => {
		this.setState({ files: [], hasFiles: false });
	};
}
